"use client";

import { useEffect, useRef, useState } from "react";

import MarkerChauffeurs from "@/assets/markers/marker-chauffeurs.png";
import MarkerConstructBedienden from "@/assets/markers/marker-construct-bedienden.png";
import MarkerConstruct from "@/assets/markers/marker-construct.png";
import MarkerFinance from "@/assets/markers/marker-finance.png";
import MarkerInterim from "@/assets/markers/marker-interim.png";
import MarkerOffice from "@/assets/markers/marker-office.png";
import MarkerTechnics from "@/assets/markers/marker-technics.png";
import { GetOfficesQuery } from "@/graphql/sdk/__generated__";
import { cn } from "@/helpers/className";
import { useGoogleMapsApi } from "@/helpers/googleMapsApi";
import { isOpenNow } from "@/helpers/openingHours";
import { generateOfficeUrl } from "@/helpers/url";
import { useTranslation } from "@/i18n/client";
import type { AllowedLanguages } from "@/types/generic";

const mapMarkers = {
  Chauffeurs: MarkerChauffeurs,
  Construct: MarkerConstruct,
  Finance: MarkerFinance,
  Interim: MarkerInterim,
  Office: MarkerOffice,
  Technics: MarkerTechnics,
  "Construct bedienden": MarkerConstructBedienden,
};

const MAP_HEIGHT = 450;

const mapStyle = [
  {
    featureType: "landscape",
    elementType: "labels",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "transit",
    elementType: "labels",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "poi",
    elementType: "labels",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "water",
    elementType: "labels",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "labels.icon",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    stylers: [
      {
        hue: "#0af",
      },
      {
        saturation: -100,
      },
      {
        gamma: 2.15,
      },
      {
        lightness: 12,
      },
    ],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [
      {
        visibility: "on",
      },
      {
        lightness: 24,
      },
    ],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [
      {
        lightness: 57,
      },
    ],
  },
];

interface OfficeMapProps {
  allOffices: NonNullable<GetOfficesQuery["offices"]>;
  className?: string;
  filteredOffices: NonNullable<GetOfficesQuery["offices"]>;
  hidden?: boolean;
  locale: AllowedLanguages;
}

interface MarkerItem {
  marker: google.maps.Marker;
  officeId: string;
}

export const OfficeMap = ({
  allOffices,
  className,
  filteredOffices,
  hidden,
  locale,
}: OfficeMapProps) => {
  const { t } = useTranslation(locale);
  const { gmapsApiIsLoaded } = useGoogleMapsApi();
  const [initialised, setInitialised] = useState(false);
  const [markers, setMarkers] = useState<MarkerItem[]>([]);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  let bounds: google.maps.LatLngBounds | undefined = undefined;

  const initMap = () => {
    if (!mapRef.current || initialised) return;

    bounds = new google.maps.LatLngBounds();
    const markerArray: MarkerItem[] = [];

    const map = new google.maps.Map(mapRef.current, {
      styles: mapStyle,
    });

    const infoWindow = new google.maps.InfoWindow();

    allOffices.forEach((office) => {
      if (!office.latitude || !office.longitude) return;

      const position = {
        lat: office.latitude,
        lng: office.longitude,
      } as google.maps.LatLngLiteral;

      const image = office.speciality
        ? mapMarkers[office.speciality]
        : undefined;

      if (!image) {
        console.error(`No marker found for ${office.speciality}`);
        return;
      }

      const icon = {
        url: image.src,
        size: new google.maps.Size(image.width, image.height),
        origin: new google.maps.Point(0, 0),
        anchor: new google.maps.Point(18, image.height),
      };

      const marker = new google.maps.Marker({
        map,
        position,
        icon,
        title: office.title,
        visible: true,
      });

      markerArray.push({
        officeId: office.id,
        marker: marker,
      });

      const i18n = {
        nl: {
          open: "Geopend",
          closed: "Gesloten",
        },
        fr: {
          open: "Ouvert",
          closed: "Fermé",
        },
        en: {
          open: "Open",
          closed: "Closed",
        },
      };

      const infoWindowContent = `<div class="prose">
        <strong class="block mb-1">${office.title}</strong>
        <div class="text-sm leading-normal">
          ${office.address.street} ${office.address.number} ${
            office.address?.box ? office.address.box : ""
          }<br>
          ${office.address.postal_code} ${office.address.locality} 
        </div>
        
        <div class="text-xs font-medium my-1">
          ${
            isOpenNow(office.opening_hours ?? [])
              ? '<span class="text-positive">' + i18n[locale].open + "</span>"
              : '<span class="text-black">' + i18n[locale].closed + "</span>"
          }        
        </div>

        <a class="link text-sm no-underline" href="${generateOfficeUrl(locale, office.title, office.id)}">${t("pages.offices.office_map.more_info")}</a>
      </div>`;

      marker.addListener("click", function () {
        infoWindow.open(map, marker);
        infoWindow.setContent(infoWindowContent);
      });

      bounds?.extend(position);

      setMarkers(markerArray);
      setMap(map);
    });

    fitBounds(bounds);
    setInitialised(true);
  };

  const fitBounds = (bounds: google.maps.LatLngBounds) => {
    if (!map) return;

    if (bounds.isEmpty()) {
      map.setZoom(8);
      map.setCenter({ lat: 50.7765396, lng: 4.7338889 });
    } else {
      map.fitBounds(bounds);
    }
  };

  if (initialised && markers) {
    bounds = new google.maps.LatLngBounds();

    markers.forEach(({ marker, officeId }) => {
      const visible = filteredOffices.some((office) => office.id === officeId);

      if (visible) {
        const markerPosition = marker.getPosition();
        if (markerPosition && bounds) {
          bounds.extend(markerPosition);
        }
      }

      marker.setVisible(
        filteredOffices.some((office) => office.id === officeId),
      );
    });

    fitBounds(bounds);
  }

  useEffect(() => {
    if (!initialised && gmapsApiIsLoaded && mapRef.current) {
      initMap();
    }
  }, [gmapsApiIsLoaded, initMap, initialised]);

  useEffect(() => {
    if (initialised && gmapsApiIsLoaded && !hidden && bounds !== undefined) {
      fitBounds(bounds);
    }
  }, [gmapsApiIsLoaded, hidden, initialised, bounds, fitBounds]);

  return (
    <div
      className={cn(
        `mb-6 h-[${MAP_HEIGHT}px] overflow-hidden rounded-xl border bg-gray-100`,
        hidden ? "hidden" : "",
        className,
      )}
    >
      {gmapsApiIsLoaded && (
        <div
          ref={mapRef}
          style={{
            width: "100%",
            height: `${MAP_HEIGHT}px`,
          }}
        ></div>
      )}
    </div>
  );
};
